// src/engine/PositionEvaluator.ts
import { Board, Piece, PieceColor, Position } from "../types/Board.ts";
import { getPieceAt, getAllPieces } from "../utils/boardUtils.ts";
import { MoveValidator } from "../utils/moveValidator.ts";
import { PIECE_VALUES } from "../data/pieceValues.ts";
import { PIECE_DEFINITIONS } from "../data/pieceDefinitions.ts";

export interface EvaluationWeights {
  materialBalance: number; // 0-1 (10%-90%)
  piecePositioning: number; // 0-1 (10%-90%)
  kingSafety: number; // 0-1 (10%-90%)
  mobility: number; // 0-1 (10%-90%)
  pawnStructure: boolean;
  boardControl: boolean;
}

export interface EvaluationResult {
  score: number;
  breakdown: {
    material: number;
    positioning: number;
    kingSafety: number;
    mobility: number;
    pawnStructure: number;
    boardControl: number;
  };
}

export class PositionEvaluator {
  private moveValidator: MoveValidator;
  private weights: EvaluationWeights;

  constructor(weights: EvaluationWeights) {
    this.moveValidator = new MoveValidator();
    this.weights = weights;
  }

  setWeights(weights: EvaluationWeights): void {
    this.weights = weights;
  }

  /**
   * Evaluate board position from perspective of given color
   * Positive score = advantage for color, Negative = disadvantage
   */
  evaluatePosition(board: Board, color: PieceColor): EvaluationResult {
    const breakdown = {
      material: this.evaluateMaterial(board, color),
      positioning: this.evaluatePositioning(board, color),
      kingSafety: this.evaluateKingSafety(board, color),
      mobility: this.evaluateMobility(board, color),
      pawnStructure: this.weights.pawnStructure ? this.evaluatePawnStructure(board, color) : 0,
      boardControl: this.weights.boardControl ? this.evaluateBoardControl(board, color) : 0,
    };

    // Calculate weighted total
    const score =
      breakdown.material * this.weights.materialBalance +
      breakdown.positioning * this.weights.piecePositioning +
      breakdown.kingSafety * this.weights.kingSafety +
      breakdown.mobility * this.weights.mobility +
      breakdown.pawnStructure +
      breakdown.boardControl;

    return { score, breakdown };
  }

  /**
   * Evaluate material balance
   */
  private evaluateMaterial(board: Board, color: PieceColor): number {
    const boardSizeStr = `${board.dimensions.rows}×${board.dimensions.rows}`;
    let myMaterial = 0;
    let opponentMaterial = 0;

    const allPieces = getAllPieces(board);

    for (const piece of allPieces) {
      const value = this.getPieceValue(piece, boardSizeStr);
      
      if (piece.color === color) {
        myMaterial += value;
      } else {
        opponentMaterial += value;
      }
    }

    return myMaterial - opponentMaterial;
  }

  /**
   * Get piece value from PIECE_VALUES
   */
  private getPieceValue(piece: Piece, boardSizeStr: string): number {
    const definition = PIECE_DEFINITIONS[piece.type.toLowerCase()];
    if (!definition) return 0;

    const abbreviation = definition.abbreviation.toUpperCase();
    const pieceValues = PIECE_VALUES[abbreviation];
    
    if (!pieceValues) return 0;

    const valueEntry = pieceValues.find(v => v.boardSize === boardSizeStr);
    return valueEntry ? valueEntry.value : 0;
  }

  /**
   * Evaluate piece positioning (center control, development)
   */
  private evaluatePositioning(board: Board, color: PieceColor): number {
    let score = 0;
    const centerRows = [
      Math.floor(board.dimensions.rows / 2) - 1,
      Math.floor(board.dimensions.rows / 2)
    ];
    const centerCols = [
      Math.floor(board.dimensions.cols / 2) - 1,
      Math.floor(board.dimensions.cols / 2)
    ];

    const allPieces = getAllPieces(board);

    for (const piece of allPieces) {
      const multiplier = piece.color === color ? 1 : -1;
      
      // Center control bonus
      const distanceFromCenter = this.getDistanceFromCenter(
        piece.position,
        board.dimensions.rows,
        board.dimensions.cols
      );
      const centerBonus = Math.max(0, 5 - distanceFromCenter) * 0.1;
      
      // Development bonus for back-rank pieces
      const developmentBonus = this.getDevelopmentBonus(piece, board);
      
      score += (centerBonus + developmentBonus) * multiplier;
    }

    return score;
  }

  private getDistanceFromCenter(pos: Position, rows: number, cols: number): number {
    const centerRow = rows / 2;
    const centerCol = cols / 2;
    return Math.abs(pos.row - centerRow) + Math.abs(pos.col - centerCol);
  }

  private getDevelopmentBonus(piece: Piece, board: Board): number {
    // Bonus for moving pieces off starting positions
    const pieceType = piece.type.toLowerCase();
    
    // Knights, bishops, and other pieces get bonus for being developed
    if (['knight', 'bishop', 'chancellor', 'archbishop'].includes(pieceType)) {
      const startingRow = piece.color === 'white' ? board.dimensions.rows - 1 : 0;
      if (piece.position.row !== startingRow) {
        return 0.2;
      }
    }
    
    return 0;
  }

  /**
   * Evaluate king/emperor safety
   */
  private evaluateKingSafety(board: Board, color: PieceColor): number {
    let score = 0;
    
    // Find king/emperor
    const king = this.findCheckmatePiece(board, color);
    if (!king) return 0;

    // Penalty for exposed king in center
    const distanceFromEdge = Math.min(
      king.position.row,
      board.dimensions.rows - 1 - king.position.row,
      king.position.col,
      board.dimensions.cols - 1 - king.position.col
    );
    
    if (distanceFromEdge > 2) {
      score -= 1.0; // King in center is dangerous
    }

    // Bonus for castling (only on 8x8 and 9x9)
    if ((board.dimensions.rows === 8 || board.dimensions.rows === 9) && king.hasMoved) {
      // Check if king moved 2 squares (castled)
      const startCol = Math.floor(board.dimensions.cols / 2);
      if (Math.abs(king.position.col - startCol) === 2) {
        score += 0.5;
      }
    }

    // Check pawn/knave/squire shield
    score += this.evaluatePawnShield(board, king);

    // Opponent king safety (negative for us)
    const opponentColor = color === 'white' ? 'black' : 'white';
    const opponentKing = this.findCheckmatePiece(board, opponentColor);
    if (opponentKing) {
      const opponentDistanceFromEdge = Math.min(
        opponentKing.position.row,
        board.dimensions.rows - 1 - opponentKing.position.row,
        opponentKing.position.col,
        board.dimensions.cols - 1 - opponentKing.position.col
      );
      
      if (opponentDistanceFromEdge > 2) {
        score += 0.5; // Opponent king exposed is good for us
      }
    }

    return score;
  }

  private findCheckmatePiece(board: Board, color: PieceColor): Piece | null {
    const allPieces = getAllPieces(board, color);
    return allPieces.find(p => 
      p.type.toLowerCase() === 'king' || p.type.toLowerCase() === 'emperor'
    ) || null;
  }

  private evaluatePawnShield(board: Board, king: Piece): number {
    let shieldScore = 0;
    const direction = king.color === 'white' ? -1 : 1;
    
    // Check squares in front of king
    for (let colOffset = -1; colOffset <= 1; colOffset++) {
      const checkPos: Position = {
        row: king.position.row + direction,
        col: king.position.col + colOffset
      };
      
      const piece = getPieceAt(board, checkPos);
      if (piece && piece.color === king.color) {
        const pieceType = piece.type.toLowerCase();
        if (['pawn', 'knave', 'squire'].includes(pieceType)) {
          shieldScore += 0.2;
        }
      }
    }
    
    return shieldScore;
  }

  /**
   * Evaluate mobility (number of legal moves)
   */
  private evaluateMobility(board: Board, color: PieceColor): number {
    const myPieces = getAllPieces(board, color);
    const opponentColor = color === 'white' ? 'black' : 'white';
    const opponentPieces = getAllPieces(board, opponentColor);

    let myMobility = 0;
    let opponentMobility = 0;

    for (const piece of myPieces) {
      const moves = this.moveValidator.getLegalMoves(piece, board);
      myMobility += moves.length;
    }

    for (const piece of opponentPieces) {
      const moves = this.moveValidator.getLegalMoves(piece, board);
      opponentMobility += moves.length;
    }

    return (myMobility - opponentMobility) * 0.1;
  }

  /**
   * Evaluate pawn structure
   */
  private evaluatePawnStructure(board: Board, color: PieceColor): number {
    let score = 0;
    const pawns = getAllPieces(board, color).filter(p => 
      ['pawn', 'knave', 'squire'].includes(p.type.toLowerCase())
    );

    // Check for doubled pawns (penalty)
    const pawnsByFile = new Map<number, number>();
    for (const pawn of pawns) {
      const count = pawnsByFile.get(pawn.position.col) || 0;
      pawnsByFile.set(pawn.position.col, count + 1);
    }

    for (const count of pawnsByFile.values()) {
      if (count > 1) {
        score -= 0.3 * (count - 1); // Penalty for doubled pawns
      }
    }

    // Check for isolated pawns (penalty)
    for (const pawn of pawns) {
      const hasNeighbor = pawns.some(p => 
        Math.abs(p.position.col - pawn.position.col) === 1
      );
      if (!hasNeighbor) {
        score -= 0.2; // Isolated pawn penalty
      }
    }

    return score;
  }

  /**
   * Evaluate board control (squares controlled)
   */
  private evaluateBoardControl(board: Board, color: PieceColor): number {
    const myPieces = getAllPieces(board, color);
    const opponentColor = color === 'white' ? 'black' : 'white';
    const opponentPieces = getAllPieces(board, opponentColor);

    const myControlled = new Set<string>();
    const opponentControlled = new Set<string>();

    for (const piece of myPieces) {
      const moves = this.moveValidator.getLegalMoves(piece, board);
      moves.forEach(pos => myControlled.add(`${pos.row},${pos.col}`));
    }

    for (const piece of opponentPieces) {
      const moves = this.moveValidator.getLegalMoves(piece, board);
      moves.forEach(pos => opponentControlled.add(`${pos.row},${pos.col}`));
    }

    return (myControlled.size - opponentControlled.size) * 0.05;
  }
}